import React from 'react'
import PropTypes from 'prop-types'
import Turbolinks from 'turbolinks'
import { prismicHelpers, urlHelpers } from '../../../helpers'
import { ChevronIcon } from '../../core/icons'
import PromoBanners from '../../promoBanners/PromoBanners'

/**
* Mobile Menu Page Component.
*
* Renders a single page of content for the Mobile Menu. Also renders children
* pages.
*/
class MobileMenuPage extends React.Component {
  constructor(props) {
    super(props)

    this.onLinkClick = this.onLinkClick.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.checkFocusability = this.checkFocusability.bind(this)
    this.renderLink = this.renderLink.bind(this)
    this.renderPageLink = this.renderPageLink.bind(this)
    this.renderDivider = this.renderDivider.bind(this)
    this.renderBanner = this.renderBanner.bind(this)
    this.renderItems = this.renderItems.bind(this)
  }

  /**
  * Handles link click event. Wraps link in an event tracker to track usage of
  * mobile menu.
  * 
  * If the link happens to have the 'openChat' context, the chat bubble is opened
  * instead of following the link location.
  * @param e {object} click event.
  * @param link {obj} link
  * @param href {string} href to set location on completion.
  */
  onLinkClick(e, link, href) {
    e.preventDefault()

    try {
      const context = JSON.parse(link.context)

      if (context.action === 'openChat') {
        if (window.CustomerSupport && typeof window.CustomerSupport.openChat === 'function') {
          window.CustomerSupport.openChat()
          this.props.toggleSidebar('Contact Us Link')
        }

        return false
      }
    } catch (error) {
      // Doesn't matter
    }

    $.track('event', 'Mobile Menu', 'Link', link.title, this.props.menuState.level, true, () => {
      Turbolinks.visit(href)
    })
  }

  /**
  * Sets the menu state to display the given level and page id. Tracks page change event.
  *
  * @param e {object} click event.
  * @param level {number} level the page changing to.
  * @param id {string} id of page changing to.
  * @param name {string} display name of page/link clicked.
  */
  onPageChange(e, level, id, name) {
    e.preventDefault()
    this.props.changeLevel(level, id)
    $.track('event', 'Mobile Menu', 'Page Change', name, this.props.menuState.level)

    return false
  }

  /**
  * Returns true if the page on the given level is currently set to visible.
  */
  checkFocusability(level, pageId) {
    return this.props.menuState.level === level && this.props.menuState.visiblePages[level] === pageId
  }

  /**
  * Renders a link to a web site.
  */
  renderLink(item, idx) {
    const isFocusable = this.checkFocusability(this.props.level, this.props.content.id)


    const ref = `mm-${this.props.content.uid}-${idx + 1}`


    const href = urlHelpers.addRef(item.link, ref)


    const isLogout = href.indexOf("/logout") > -1

    return (
      <li
        key={idx}
        className={`is-clickable ${item.subtitle ? 'has-subtitle' : ''}`}
        role="menuitem"
      >
        <a
          href={href}
          data-method={isLogout ? 'delete' : undefined}
          tabIndex={isFocusable ? '0' : '-1'}
          onClick={e => this.onLinkClick(e, item, href)}
        >
          <div className="is-title">{ item.title }</div>
          { item.subtitle
            ? <div className="is-subtitle">{ item.subtitle }</div>
            : false
          }
        </a>
      </li>
    )
  }

  /**
  * Renders a link to another mobile menu page. Also renders the linked mobile
  * menu page.
  */
  renderPageLink(item, idx) {
    const isFocusable = this.checkFocusability(this.props.level, this.props.content.id)


    const childrenAreFocusable = this.checkFocusability(this.props.level + 1, item.link.id)


    const backButtonLabel = `Back to ${this.props.level ? this.props.previousTitle : 'Main Menu'}`

    // Only render items that are visible.
    item.link.items = item.link.items
      .filter(child => prismicHelpers.userHasPermission(child))

    // Don't render the link or child page if there will be nothing to show on
    // the next page.
    if (!item.link.items.length) {
      return false
    }

    return (
      <li
        key={idx}
        className={`is-clickable ${item.subtitle ? 'has-subtitle' : ''}`}
        role="menuitem"
        aria-haspopup="true"
        aria-expanded={childrenAreFocusable}
      >
        <a
          href="#"
          onClick={e => this.onPageChange(e, this.props.level + 1, item.link.id, item.title)}
          tabIndex={isFocusable ? '0' : '-1'}
        >
          <div className="is-title">{ item.title }</div>
          { item.subtitle
            ? <div className="is-subtitle">{ item.subtitle }</div>
            : false
          }
          <ChevronIcon isRight className="is-right-icon" />
        </a>

        <MobileMenuPage
          content={item.link}
          changeLevel={this.props.changeLevel}
          level={this.props.level + 1}
          menuState={this.props.menuState}
          previousTitle={item.title}
        >
          <ul className="default-list">
            <li className={`${item.subtitle ? 'has-subtitle' : ''} text-center`} role="none">
              <span>
                <div className="is-title">{ item.title }</div>
                { item.subtitle
                  ? <div className="is-subtitle">{ item.subtitle }</div>
                  : false
                }
              </span>
            </li>
            <li className="is-clickable" role="menuitem">
              <a
                href="#"
                onClick={e => this.onPageChange(e, this.props.level, this.props.content.id, backButtonLabel)}
                tabIndex={childrenAreFocusable ? '0' : '-1'}
              >
                <ChevronIcon isLeft className="is-left-icon" />
                { backButtonLabel }
              </a>
            </li>
          </ul>
        </MobileMenuPage>
      </li>
    )
  }

  /**
  * Renders a divider.
  */
  renderDivider(item, idx) {
    return (
      <li key={idx} role="none">
        <hr />
      </li>
    )
  }

  /**
  * Renders a promo banner.
  */
  renderBanner(item, idx) {
    item.promoPosition = `item_${idx + 1}`
    const data = {
      banners: [item],
      uid: item.promoId,
      promoName: item.promoName,
    }


    const isVisible = this.checkFocusability(this.props.level, this.props.content.id)

    return (
      <li key={idx} role="menuitem">
        <PromoBanners
          data={data}
          key={idx}
          overrideVisibleCheck
          isVisible={isVisible}
          type="simple"
        />
      </li>
    )
  }

  /**
  * Renders each item in the mobile menu page.
  */
  renderItems() {
    return this.props.content.items
      .map((item, idx) => {
        switch (item.type) {
          case 'link':
            if (typeof item.link === 'object') {
              return this.renderPageLink(item, idx)
            }

            return this.renderLink(item, idx)

          case 'divider':
            return this.renderDivider(item, idx)
          case '4x1':
            return this.renderBanner(item, idx)
        }
      })
  }

  render() {
    const pageIsVisible = this.props.menuState.visiblePages[this.props.level] === this.props.content.id

    return (
      <div
        className={`mobile-menu-page ${pageIsVisible ? 'is-visible' : 'is-hidden'}`}
        aria-hidden={!pageIsVisible}
        role="menu"
      >
        { this.props.children ? (
          <header>
            { this.props.children }
          </header>) : false
        }
        <div className="is-scrollable">
          <ul className="default-list">
            { this.renderItems() }
          </ul>
        </div>
      </div>
    )
  }
}

MobileMenuPage.defaultProps = {
  level: 0,
  previousTitle: '',
}

MobileMenuPage.propTypes = {
  content: PropTypes.object.isRequired,
  changeLevel: PropTypes.func,
  level: PropTypes.number,
  previousTitle: PropTypes.string,
  toggleSidebar: PropTypes.func.isRequired,
}

export default MobileMenuPage
